<script setup lang="ts">
/**
 * Searchbar enrichie pour /recherche (Karim 2026-06-25).
 *
 * Inspiré du panel HelloAsso : 2 champs (Quoi + Où) côte à côte qui
 * ouvrent un panel large au focus avec :
 *  - section « … par thème » (vignettes photo + nombre de résultats)
 *  - résultats live (debounced) au fur et à mesure de la frappe
 *  - champ Où : autocompletion ville depuis availableCities
 *
 * Fermeture :
 *  - clic en dehors du composant
 *  - touche Escape
 *  - sélection d'un item du panel (auto-close + navigate)
 *
 * Le composant utilise les states partagés `useRechercheSearchQuery()`
 * pour la query Quoi et un nouvel useState pour la ville sélectionnée
 * + un emit `select-ville` (pour la cohérence avec le filtre existant).
 */
import { RECHERCHE_THEMES } from "~/composables/useRechercheHelpers";
import { useRechercheVille } from "~/composables/useRechercheVille";
import type { RayonThemes } from "~/services/api/rechercheApi";
import { useComptesThemes } from "~/data/queries/recherche.queries";
import { isListingPath, LISTING_BASE_PATH } from "#shared/utils/listingTypes";

const { t } = useI18n();

interface CityOption {
  slug: string;
  name: string;
  count: number;
}

interface LiveResult {
  type: "association" | "cagnotte" | "evenement" | "adhesion";
  title: string;
  subtitle?: string;
  href: string;
  image?: string | null;
}

const props = defineProps<{
  /** Liste des villes disponibles (depuis page parente). */
  cities?: CityOption[];
  /** Ville actuellement sélectionnée (peut venir de l'URL). */
  selectedVille?: string | null;
  /** Résultats live (passé par parent depuis ses computed). */
  liveResults?: LiveResult[];
}>();

const emit = defineEmits<{
  "select-ville": [slug: string | null];
}>();

const searchQuery = useRechercheSearchQuery();
const router = useRouter();

// ─── Refs UI ──────────────────────────────────────────────────────────
const rootRef = ref<HTMLElement | null>(null);
const quoiInput = ref<HTMLInputElement | null>(null);
const ouInput = ref<HTMLInputElement | null>(null);
const isOpen = ref(false);
const activeField = ref<"quoi" | "ou" | null>(null);

// Champ Où : logique partagée avec la fenêtre « Filtres » (useRechercheVille).
// `ouQuery` = saisie locale (libre), pré-remplie avec la ville sélectionnée.
const {
  requete: ouQuery,
  suggestions: citySuggestions,
  enCours: citySearchPending,
  geoEnCours: geoLoading,
  geoErreur: geoError,
  localiser,
} = useRechercheVille(() => props.cities);

// Initialisation : si une ville est déjà sélectionnée à l'init, on
// pré-remplit l'input Où avec son nom.
watchEffect(() => {
  if (props.selectedVille && props.cities) {
    const c = props.cities.find((x) => x.slug === props.selectedVille);
    if (c) ouQuery.value = c.name;
  } else if (!props.selectedVille) {
    ouQuery.value = "";
  }
});

const open = (field: "quoi" | "ou") => {
  isOpen.value = true;
  activeField.value = field;
  // Le mode expanded est rendu via v-else → les inputs n'existent qu'au
  // tick suivant. nextTick pour les focus correctement.
  nextTick(() => {
    if (field === "quoi") quoiInput.value?.focus();
    else ouInput.value?.focus();
  });
};

const close = () => {
  isOpen.value = false;
  activeField.value = null;
};

// ─── Fermeture clic-dehors + Escape ──────────────────────────────────
const onDocClick = (e: MouseEvent) => {
  if (!isOpen.value || !rootRef.value) return;
  if (!rootRef.value.contains(e.target as Node)) close();
};
const onKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape" && isOpen.value) {
    close();
    quoiInput.value?.blur();
    ouInput.value?.blur();
  }
};

onMounted(() => {
  if (import.meta.client) {
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeydown);
  }
});
onBeforeUnmount(() => {
  if (import.meta.client) {
    document.removeEventListener("mousedown", onDocClick);
    document.removeEventListener("keydown", onKeydown);
  }
});

const pickCity = (city: {
  name: string;
  slug: string;
}) => {
  ouQuery.value = city.name;
  emit("select-ville", city.slug);
  // On garde le panel ouvert pour permettre de chercher du « Quoi ».
  activeField.value = "quoi";
  quoiInput.value?.focus();
};

const clearVille = () => {
  ouQuery.value = "";
  emit("select-ville", null);
};

// ─── « Ma position » (Karim 2026-06-25) — géolocalisation partagée ───
const useMyPosition = async () => {
  const ville = await localiser();
  if (ville) pickCity(ville);
};

// ─── Thèmes en photos (2026-09-26) ───────────────────────────────────
// Les vignettes des thèmes (celles de l'ancienne fenêtre « Filtres ») mènent
// au rayon où l'on EST (associations par défaut) — 🔴 avant, « Sport » sur la
// page Associations envoyait vers les événements. Elles remplacent les cartes
// « idées d'activités » : le thème est la catégorie connue de TOUT ce qui se
// cherche (associations, cagnottes, événements, adhésions, boutiques).
const route = useRoute();

/**
 * Rayon des vignettes : celui de la page — liste OU page publique du même
 * type (cagnotte /c/, billetterie /b/, adhésion /a/, boutique) —,
 * associations sinon.
 */
const rayonThemes = computed<{ rayon: RayonThemes; path: string; titre: string; nom: string }>(() => {
  const p = route.path;
  if (p.startsWith(LISTING_BASE_PATH.cagnottes) || p.startsWith("/c/")) {
    return { rayon: "cagnottes", path: LISTING_BASE_PATH.cagnottes, titre: t("association.recherche.titreCagnottes"), nom: t("association.recherche.nomCagnottes") };
  }
  if (p.startsWith(LISTING_BASE_PATH.evenements) || p.startsWith("/b/")) {
    return { rayon: "evenements", path: LISTING_BASE_PATH.evenements, titre: t("association.recherche.titreEvenements"), nom: t("association.recherche.nomEvenements") };
  }
  if (p.startsWith(LISTING_BASE_PATH.adhesions) || p.startsWith("/a/")) {
    return { rayon: "adhesions", path: LISTING_BASE_PATH.adhesions, titre: t("association.recherche.titreAdhesions"), nom: t("association.recherche.nomAdhesions") };
  }
  if (p.startsWith(LISTING_BASE_PATH.boutiques) || p.startsWith("/boutique/")) {
    return { rayon: "boutiques", path: LISTING_BASE_PATH.boutiques, titre: t("association.recherche.titreBoutiques"), nom: t("association.recherche.nomBoutiques") };
  }
  return { rayon: "associations", path: LISTING_BASE_PATH.associations, titre: t("association.recherche.titreAssociations"), nom: t("association.recherche.nomAssociations") };
});

/**
 * Hors d'une liste (page d'une association, d'une cagnotte, d'un
 * événement…), rien ne filtre le texte tapé ni la ville sur place : la
 * recherche mène à la liste du rayon (`?q=` pré-remplit la barre,
 * cf. ListingView).
 */
const surListe = computed(() => isListingPath(route.path));
const texteTape = computed(() => searchQuery.value?.trim() ?? "");
const lancerRecherche = () => {
  const query: Record<string, string> = {};
  if (texteTape.value) query.q = texteTape.value;
  if (props.selectedVille) query.ville = props.selectedVille;
  close();
  void router.push({ path: rayonThemes.value.path, query });
};
const libelleVille = computed(
  () => props.cities?.find((c) => c.slug === props.selectedVille)?.name ?? ouQuery.value.trim(),
);

// Nombre de résultats par thème dans le rayon : chargé à l'ouverture du
// panneau seulement, gardé 5 min (TanStack, cf. useComptesThemes).
const { data: comptesThemes } = useComptesThemes({
  rayon: () => rayonThemes.value.rayon,
  enabled: isOpen,
});

/** Les plus fournis d'abord ; « Autre » n'est pas un domaine qu'on cherche. */
const themesPanneau = computed(() => {
  const comptes = comptesThemes.value;
  const themes = RECHERCHE_THEMES.filter((t) => t.value !== "all" && t.value !== "autre");
  if (!comptes) return themes;
  return [...themes].sort((x, y) => (comptes[y.value] ?? 0) - (comptes[x.value] ?? 0));
});

// Navigation panel : on remplit le champ Quoi avec le libellé choisi + on
// navigue vers le bon rayon avec la ville préservée (Karim 2026-06-25).
const navigateWithVille = (
  path: string,
  filtre: { category?: string },
  label: string,
) => {
  const query: Record<string, string> = {};
  if (filtre.category) query.category = filtre.category;
  if (props.selectedVille) query.ville = props.selectedVille;
  close();
  // Libellé posé APRÈS la navigation : le filtre est alors déjà actif et la
  // recherche texte ignorée. Posé avant, une requête « Sport » cherchée dans
  // les NOMS partait pour rien juste avant la bonne.
  void router.push({ path, query }).then(() => {
    searchQuery.value = label;
  });
};

const goToTheme = (theme: { value: string; label: string }) => {
  navigateWithVille(rayonThemes.value.path, { category: theme.value }, themeAffiche(theme.value));
};

// ─── Soumission Quoi ─────────────────────────────────────────────────
// On laisse la query live dans le state partagé — pas besoin de submit.
// Mais on prévoit Enter pour fermer le panel (le filtrage est déjà actif).
const onQuoiEnter = () => {
  if (!surListe.value && (texteTape.value || props.selectedVille)) {
    lancerRecherche();
    return;
  }
  close();
  quoiInput.value?.blur();
};

// Quand l'utilisateur clique sur un résultat live, on ferme + navigue.
const goToResult = (r: LiveResult) => {
  close();
  // href est probablement absolu (ex /associations/slug) → navigateTo
  navigateTo(r.href);
};

// Le panel est ouvert mais sans contenu utile si Quoi est vide :
// dans ce cas on montre les thèmes du rayon.
// Si Quoi non vide : on montre les résultats live (si dispo).
//
// Un clic sur une vignette écrit son libellé (« Sport ») dans le champ : ce
// texte n'est pas une recherche tapée. En rouvrant le panneau pour CHANGER
// de thème, on doit revoir les vignettes, pas des résultats pour « Sport ».
const texteEstUnTheme = computed(() => {
  const q = searchQuery.value?.trim().toLowerCase();
  // Libellé français (liens anciens) ou affiché dans la langue de la page.
  return (
    !!q &&
    RECHERCHE_THEMES.some(
      (t) => t.label.toLowerCase() === q || themeAffiche(t.value).toLowerCase() === q,
    )
  );
});
const showLiveResults = computed(
  () =>
    // Hors liste, les résultats en direct viendraient de la DERNIÈRE liste
    // visitée, pas du texte tapé ici.
    surListe.value &&
    !texteEstUnTheme.value &&
    !!searchQuery.value &&
    searchQuery.value.trim().length > 0 &&
    (props.liveResults?.length ?? 0) > 0,
);
const showSections = computed(
  () =>
    !surListe.value ||
    texteEstUnTheme.value ||
    !searchQuery.value ||
    searchQuery.value.trim().length === 0,
);

/** Thèmes actifs dans l'URL (`?category=sport,loisirs`) : vignettes cochées. */
const themesActifs = computed(() => {
  const c = route.query.category;
  const brut = (Array.isArray(c) ? c.join(",") : c) ?? "";
  return new Set(brut.split(",").filter(Boolean));
});
</script>

<template>
  <!-- Largeur dynamique (Karim 2026-06-25) :
       - collapsed = max-w-md (mêmes dimensions que la searchbar simple d'avant)
       - expanded  = max-w-2xl (place suffisante pour les 2 champs Quoi/Où) -->
  <div
    ref="rootRef"
    class="relative w-full transition-[max-width] duration-200 ease-out"
    :class="isOpen ? 'max-w-2xl' : 'max-w-md'"
  >
    <!-- ═══════════════════════════════════════════════════════════════
         MODE COLLAPSED (par défaut) : searchbar simple comme avant.
         Au clic n'importe où dessus → bascule en mode expanded + ouvre
         le panel + focus auto sur le champ Quoi.
         ═══════════════════════════════════════════════════════════════ -->
    <div
      v-if="!isOpen"
      class="flex items-center rounded-full border border-gray-400 bg-white hover:border-gray-600 transition-colors pl-4 pr-1.5 py-1.5 md:py-1 cursor-text"
      @click="open('quoi')"
    >
      <input
        :value="searchQuery"
        type="text"
        :placeholder="$t('association.recherche.quoiPlaceholder')"
        class="flex-1 min-w-0 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none cursor-text"
        readonly
        @focus="open('quoi')"
      />
      <!-- Bouton "Search" (texte, Karim 2026-07-09). py-2.5 sur mobile pour
           remplir la hauteur de la searchbar sans l'écraser ; py-2.5 dès md.
           `--cta-header` : couleur de l'asso, posée par AppHeader sur une
           adhésion / un événement / une boutique ; noir ailleurs, comme
           « Inscrire mon association » (Karim 2026-11-03). -->
      <button
        type="button"
        class="inline-flex items-center px-4 md:px-5 py-2.5 rounded-full bg-[var(--cta-header,#000)] text-[color:var(--cta-header-text,#fff)] text-sm font-semibold hover:opacity-90 transition-opacity shrink-0"
        @click.stop="open('quoi')"
      >
        {{ $t("association.introuvable.boutonRechercher") }}
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════
         MODE EXPANDED (au clic) : double champ Quoi + Où, séparateur,
         bordure main + shadow. Le panel ci-dessous est ouvert simulta-
         nément (même condition `isOpen`).
         ═══════════════════════════════════════════════════════════════ -->
    <div
      v-else
      class="flex items-stretch rounded-full border border-[var(--cta-header,var(--color-main))] bg-white shadow-lg shadow-main/10 overflow-hidden"
    >
      <!-- Quoi -->
      <label
        class="flex-1 min-w-0 flex items-center pl-5 pr-3 py-2 cursor-text"
        :class="activeField === 'quoi' ? 'bg-gray-50/60' : ''"
        @click="quoiInput?.focus()"
      >
        <div class="flex-1 min-w-0">
          <div class="text-[11px] font-semibold text-gray-900 leading-tight">{{ $t("association.recherche.quoi") }}</div>
          <input
            ref="quoiInput"
            v-model="searchQuery"
            type="text"
            :placeholder="$t('association.recherche.quoiPlaceholder')"
            class="w-full bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none mt-0.5"
            @focus="activeField = 'quoi'"
            @keydown.enter.prevent="onQuoiEnter"
          />
        </div>
        <button
          v-if="searchQuery"
          type="button"
          :aria-label="$t('association.recherche.effacerRecherche')"
          class="text-gray-400 hover:text-gray-600 mr-1"
          @click.stop="searchQuery = ''"
        >
          <UIcon name="i-lucide-x" class="size-4" />
        </button>
      </label>

      <!-- Séparateur visuel -->
      <div class="w-px bg-gray-200 my-2" />

      <!-- Où -->
      <label
        class="flex-1 min-w-0 flex items-center pl-5 pr-3 py-2 cursor-text"
        :class="activeField === 'ou' ? 'bg-gray-50/60' : ''"
        @click="ouInput?.focus()"
      >
        <div class="flex-1 min-w-0">
          <div class="text-[11px] font-semibold text-gray-900 leading-tight">{{ $t("association.recherche.ou") }}</div>
          <input
            ref="ouInput"
            v-model="ouQuery"
            type="text"
            :placeholder="$t('association.recherche.ouPlaceholder')"
            class="w-full bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none mt-0.5"
            @focus="activeField = 'ou'"
          />
        </div>
        <button
          v-if="ouQuery"
          type="button"
          :aria-label="$t('association.recherche.effacerVille')"
          class="text-gray-400 hover:text-gray-600 mr-1"
          @click.stop="clearVille"
        >
          <UIcon name="i-lucide-x" class="size-4" />
        </button>
      </label>

      <!-- Bouton "Search" (texte, Karim 2026-07-09). -->
      <button
        type="button"
        class="m-1 inline-flex items-center px-4 md:px-5 rounded-full bg-[var(--cta-header,#000)] text-[color:var(--cta-header-text,#fff)] text-sm font-semibold hover:opacity-90 transition-opacity shrink-0"
        @click="close"
      >
        {{ $t("association.introuvable.boutonRechercher") }}
      </button>
    </div>

    <!-- ─── Panel ──────────────────────────────────────────────────── -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="isOpen"
        class="absolute top-full left-0 right-0 mt-3 bg-white rounded-3xl shadow-2xl shadow-black/10 border border-gray-100 max-h-[75vh] overflow-y-auto z-50"
      >
        <!-- ── Mode champ Où actif : autocompletion villes ── -->
        <div v-if="activeField === 'ou'" class="p-4">
          <RechercheVilleSuggestions
            :requete="ouQuery"
            :suggestions="citySuggestions"
            :en-cours="citySearchPending"
            :geo-en-cours="geoLoading"
            :geo-erreur="geoError"
            @choisir="pickCity"
            @ma-position="useMyPosition"
          />
        </div>

        <!-- ── Mode Quoi avec résultats live ── -->
        <div v-else-if="showLiveResults" class="p-4">
          <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-2">
            {{ $t("association.recherche.resultats") }}
          </h3>
          <div class="flex flex-col">
            <button
              v-for="(r, i) in liveResults"
              :key="`${r.type}-${i}-${r.href}`"
              type="button"
              class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left"
              @click="goToResult(r)"
            >
              <div class="size-10 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
                <img
                  v-if="r.image"
                  :src="r.image"
                  :alt="r.title"
                  class="w-full h-full object-cover"
                  loading="lazy"
                />
                <UIcon
                  v-else
                  :name="
                    r.type === 'association' ? 'i-lucide-building-2' :
                    r.type === 'cagnotte' ? 'i-lucide-heart' :
                    r.type === 'evenement' ? 'i-lucide-calendar-days' :
                    'i-lucide-users'
                  "
                  class="size-4 text-gray-500"
                />
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-gray-900 truncate">{{ r.title }}</div>
                <div v-if="r.subtitle" class="text-xs text-gray-500 truncate">{{ r.subtitle }}</div>
              </div>
              <span class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide shrink-0">
                {{ r.type }}
              </span>
            </button>
          </div>
        </div>

        <!-- ── Mode défaut : les thèmes du rayon, en photos ── -->
        <!-- Téléphone : deux rangées qui défilent (tout tient dans les 75vh) ;
             ordinateur : 3 rangées de 6. -->
        <div v-else-if="showSections" class="p-4 sm:p-6">
          <!-- Hors liste : le texte / la ville mènent à la liste du rayon. -->
          <button
            v-if="!surListe && ((texteTape && !texteEstUnTheme) || selectedVille)"
            type="button"
            class="w-full flex items-center gap-3 px-3 py-2.5 mb-4 rounded-xl bg-main/5 hover:bg-main/10 transition-colors text-left"
            @click="lancerRecherche"
          >
            <div class="size-9 rounded-full bg-main/10 flex items-center justify-center shrink-0">
              <UIcon name="i-lucide-search" class="size-4 text-main" />
            </div>
            <span class="flex-1 min-w-0 text-sm font-medium text-main truncate">
              <template v-if="texteTape && !texteEstUnTheme">{{
                selectedVille && libelleVille
                  ? $t("association.recherche.rechercherDansVille", { texte: texteTape, rayon: rayonThemes.nom, ville: libelleVille })
                  : $t("association.recherche.rechercherDans", { texte: texteTape, rayon: rayonThemes.nom })
              }}</template>
              <template v-else>{{
                selectedVille && libelleVille
                  ? $t("association.recherche.voirToutDansVille", { rayon: rayonThemes.nom, ville: libelleVille })
                  : $t("association.recherche.voirToutDans", { rayon: rayonThemes.nom })
              }}</template>
            </span>
            <UIcon name="i-lucide-arrow-right" class="size-4 text-main shrink-0" />
          </button>
          <section>
            <h3 class="text-sm sm:text-base font-bold text-gray-900 mb-3 sm:mb-4">
              {{ rayonThemes.titre }}
            </h3>
            <div
              class="grid grid-rows-2 grid-flow-col auto-cols-[40%] sm:grid-rows-none sm:grid-flow-row sm:grid-cols-6 gap-2.5 sm:gap-3 overflow-x-auto sm:overflow-visible snap-x snap-mandatory -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 sm:pb-0 [scrollbar-width:none]"
            >
              <button
                v-for="theme in themesPanneau"
                :key="theme.value"
                type="button"
                :aria-pressed="themesActifs.has(theme.value)"
                :class="[
                  'group relative snap-start rounded-2xl overflow-hidden aspect-[3/2] bg-gray-100 transition-shadow',
                  themesActifs.has(theme.value) ? 'ring-2 ring-main ring-offset-2' : 'hover:shadow-md',
                ]"
                @click="goToTheme(theme)"
              >
                <img
                  v-if="theme.image"
                  :src="theme.image"
                  :alt="themeAffiche(theme.value)"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <span class="absolute bottom-1.5 left-2 right-2 text-center text-white font-semibold text-xs sm:text-[13px] leading-tight line-clamp-2">
                  {{ themeAffiche(theme.value) }}
                </span>
                <span
                  v-if="comptesThemes"
                  class="absolute top-1.5 right-1.5 min-w-5 h-5 px-1.5 rounded-full bg-black/45 backdrop-blur-sm text-white text-[11px] font-semibold leading-5 text-center tabular-nums"
                >
                  {{ comptesThemes[theme.value] ?? 0 }}
                </span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </Transition>
  </div>
</template>
